import {ChartColor} from '@wallaby-ui/document';
import {useCallback, useRef, useState} from 'react';

/**
 * Backs a clickable chart legend (LightChartLegend2). Keeps a mutable map of
 * hidden series keyed by ChartColor.id and a nonce that lets callers recompute
 * their `chartColors` (and thus the chart traces) when the legend toggles a
 * series. Mirrors the inline pattern used by the Pathogen detail and
 * "HAIs by pathogen" charts, extracted so the report/dashboard charts can reuse it.
 *
 * Usage:
 *   const {hiddenMap, onSetHiddenMap, applyHidden} = useChartHiddenMap();
 *   const chartColors = useMemo(() => applyHidden([...]), [applyHidden, ...deps]);
 *   <LightChartLegend2 items={chartColors} hiddenMap={hiddenMap} onSetHiddenMap={onSetHiddenMap} />
 */
export function useChartHiddenMap() {
  const hiddenMap = useRef({} as Record<string | number, boolean>);
  const [hiddenMapNonce, setNonce] = useState(0);
  const onSetHiddenMap = useCallback(() => setNonce((v) => v + 1), []);
  // Stamps each color with its current hidden state. Identity changes with the
  // nonce so a useMemo depending on `applyHidden` recomputes after every toggle.
  const applyHidden = useCallback(
    (colors: ChartColor[]): ChartColor[] =>
      colors.map((color, i) => {
        const id = color.id ?? color.label ?? i;
        return {...color, id, hidden: hiddenMap.current[id]};
      }),
    [hiddenMapNonce] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return {hiddenMap, onSetHiddenMap, applyHidden, hiddenMapNonce};
}
